import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Mail, Loader2 } from "lucide-react";
import { buildQuoteHtml } from "@/lib/quote";
import { printRfp } from "@/lib/rfp";
import { fmtMoney } from "@/lib/pricing";
import { useQuoteEmail, agentCcLabel } from "@/hooks/useQuoteEmail";
import { TeamSend } from "@/components/TeamSend";
import { toast } from "sonner";

export default function QuoteDocModal({ open, onOpenChange, rfpData, result }) {
  const options = [result?.inhouse, result?.broker, result?.outsourced].filter((o) => o && o.available);
  const [methodKey, setMethodKey] = useState("");
  const [scope, setScope] = useState("full"); // "full" or split index as string

  const splits = rfpData.stopSplits || [];

  const {
    emails,
    setEmails,
    ccOwner,
    setCcOwner,
    ccAgent,
    setCcAgent,
    attachPdf,
    setAttachPdf,
    sending,
    agentEmails,
    recipients,
    sendEmail,
  } = useQuoteEmail(rfpData);

  const ccLabel = useMemo(() => agentCcLabel(agentEmails), [agentEmails]);

  const chosen =
    options.find((o) => o.key === methodKey) ||
    options.find((o) => o.key === result?.recommendation?.key) ||
    options[0];

  const shareIdx = scope === "full" ? -1 : Number(scope);
  const share = shareIdx >= 0 ? splits[shareIdx] : null;

  // Share scope: a mini quote for one customer's drop and their portion of the delivery price.
  // Only that customer's equipment is included (unassigned units show on every quote).
  const shareCustomerName = share
    ? ((rfpData.customers || [])[shareIdx]?.name || share.customer || "").trim()
    : "";
  const html = chosen
    ? share
      ? buildQuoteHtml({
          ...rfpData,
          stops: [share.address],
          customers: [
            (rfpData.customers || [])[shareIdx]?.name || (rfpData.customers || [])[shareIdx]?.address
              ? rfpData.customers[shareIdx]
              : { name: share.customer, email: "", phone: "", address: "" },
          ],
          units: (rfpData.units || []).filter(
            (u) => !(u.customer || "").trim() || u.customer.trim() === shareCustomerName
          ),
          mowerCost: "",
          stopSplits: [],
          stopEtas:
            chosen.key === "inhouse"
              ? (rfpData.stopEtas || []).filter((e) => e.address === share.address)
              : [],
          price: share.share,
          methodLabel: `${chosen.label} — ${share.customer || `Stop ${shareIdx + 1}`} share (${share.pct}% of route)`,
        })
      : buildQuoteHtml({
          ...rfpData,
          price: chosen.price,
          methodLabel: chosen.label,
          stopEtas: chosen.key === "inhouse" ? rfpData.stopEtas : [],
        })
    : "";

  const handlePrint = () => {
    const ok = printRfp(html);
    if (!ok) toast.error("Please allow pop-ups to print / save the quote as PDF");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-5 border-b border-border">
          <DialogTitle className="text-[#1C1917]">Customer Delivery Quote</DialogTitle>
          <DialogDescription>
            A customer-facing quote (no internal costs or margins). Download it, or email it to the customer or finance director.
          </DialogDescription>
        </DialogHeader>

        {!chosen ? (
          <p className="p-6 text-sm text-[#57534E]" data-testid="quote-doc-empty">
            No priced option yet — calculate a route or enter a broker / vendor quote first.
          </p>
        ) : (
          <>
            <div className="px-5 py-3 bg-stone-50 border-b border-border space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                {splits.length > 1 && (
                  <div className="min-w-[210px]">
                    <Label className="text-[11px] font-bold uppercase text-[#57534E]">Quote scope</Label>
                    <Select
                      value={scope}
                      onValueChange={(v) => {
                        setScope(v);
                        const c = rfpData.customers?.[Number(v)];
                        if (v !== "full" && c?.email) setEmails((prev) => prev || c.email);
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="quote-doc-scope-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full" data-testid="quote-doc-scope-full">
                          Full quote (all drops)
                        </SelectItem>
                        {splits.map((sp, i) => (
                          <SelectItem key={`${sp.address}-${i}`} value={String(i)} data-testid={`quote-doc-scope-share-${i}`}>
                            {sp.customer || `Stop ${i + 1}`} share — {fmtMoney(sp.share)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="min-w-[200px]">
                  <Label className="text-[11px] font-bold uppercase text-[#57534E]">Price from</Label>
                  <Select value={chosen.key} onValueChange={setMethodKey}>
                    <SelectTrigger className="mt-1" data-testid="quote-doc-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((o) => (
                        <SelectItem key={o.key} value={o.key} data-testid={`quote-doc-method-${o.key}`}>
                          {o.label} — {fmtMoney(o.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-[11px] font-bold uppercase text-[#57534E]">
                    Send to (customer / finance director)
                  </Label>
                  <Input
                    type="text"
                    placeholder="customer@example.com, finance@kgmowers.com"
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    className="mt-1 focus-visible:ring-2 focus-visible:ring-[#F97316]/30"
                    data-testid="quote-doc-email-input"
                  />
                </div>
                <Button onClick={handlePrint} className="bg-[#1C1917] hover:bg-black text-white shrink-0" data-testid="quote-doc-print-button">
                  <Printer className="h-4 w-4 mr-2" /> Download / Print
                </Button>
                <Button
                  onClick={() => sendEmail(html)}
                  disabled={sending}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white shrink-0"
                  data-testid="quote-doc-send-button"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  <span className="ml-1.5">Send{recipients.length ? ` (${recipients.length})` : ""}</span>
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="quote-doc-attach-pdf">
                  <Checkbox checked={attachPdf} onCheckedChange={(v) => setAttachPdf(!!v)} />
                  <span className="text-xs text-[#1C1917]">Include PDF</span>
                </label>
                {ccLabel.count > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="quote-doc-cc-agent">
                    <Checkbox checked={ccAgent} onCheckedChange={(v) => setCcAgent(!!v)} />
                    <span className="text-xs text-[#1C1917]">
                      Copy sales agent{ccLabel.count > 1 ? "s" : ""} ({ccLabel.text})
                    </span>
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer" data-testid="quote-doc-cc-owner">
                  <Checkbox checked={ccOwner} onCheckedChange={(v) => setCcOwner(!!v)} />
                  <span className="text-xs text-[#1C1917]">Copy the owner (ike@kgmowers.com)</span>
                </label>
              </div>
              <TeamSend html={html} attachPdf={attachPdf} />
            </div>

            <div className="overflow-auto p-4 bg-stone-200" data-testid="quote-doc-preview">
              <iframe
                title="Quote Preview"
                srcDoc={html}
                className="w-full bg-white rounded-md border border-border"
                style={{ height: "50vh" }}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
